import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Home.css';
import Modal from '../components/Modal';

const Home = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    experience: '',
    previousBike: '',
    budget: '1.5',
    preferences: '',
  });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    message: '',
    type: 'warning',
  });

  const totalSteps = 4;
  const stepsTitle = ['Riding Experience', 'Previous Ride', 'Budget', 'Preferences'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const showModal = (message, type = 'warning') => {
    setModal({
      isOpen: true,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModal({
      ...modal,
      isOpen: false,
    });
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.experience || isNaN(formData.experience) || parseInt(formData.experience) < 0 || parseInt(formData.experience) > 50) {
        showModal('Please enter a valid experience between 0 and 50 years', 'warning');
        return;
      }

      // If experience is 0, skip step 2 and go directly to step 3
      if (parseInt(formData.experience) === 0) {
        setCurrentStep(3);
        return;
      }
    }

    if (currentStep === 2 && parseInt(formData.experience) > 0) {
      if (!formData.previousBike) {
        showModal('Please enter your previous bike model', 'warning');
        return;
      }
    }

    if (currentStep === 3) {
      if (!formData.budget || isNaN(parseFloat(formData.budget)) || parseFloat(formData.budget) < 1.5 || parseFloat(formData.budget) > 50) {
        showModal('Please select a valid budget between 1.5 and 50 lakhs', 'warning');
        return;
      }
    }

    const newStep = currentStep + 1;
    setCurrentStep(newStep);
  };

  const prevStep = () => {
    // If we're on step 3 and experience is 0, go back to step 1 (skip step 2)
    if (currentStep === 3 && parseInt(formData.experience) === 0) {
      setCurrentStep(1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only process submission on the final step
    if (currentStep !== 4) {
      return;
    }

    setLoading(true);

    // Pass the form data and loading state to the next page via state
    navigate('/recommendations', { state: { formData, isLoading: true } });
  };

  const renderProgressIndicator = () => {
    return (
      <div className='progress-indicator'>
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className={`step ${currentStep > i ? 'completed' : ''} ${currentStep === i + 1 ? 'active' : ''}`}>
            <div className='step-number'>
              {currentStep > i ? (
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z' />
                </svg>
              ) : (
                i + 1
              )}
            </div>
            <div className='step-label'>{stepsTitle[i]}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderForm = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className='form-step'>
            <h2>How many years of riding experience do you have?</h2>
            <p className='form-description'>We'll tailor recommendations based on your expertise level</p>
            <div className='form-group'>
              <input type='number' className='form-control' name='experience' value={formData.experience} onChange={handleInputChange} min='0' max='50' placeholder='Enter years (0-50)' required />
            </div>
            <div className='form-actions'>
              <button type='button' className='btn btn-primary' onClick={nextStep}>
                Continue
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H4.5z' />
                </svg>
              </button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className='form-step'>
            <h2>Which bike have you previously owned/ridden extensively?</h2>
            <p className='form-description'>This helps us understand your previous riding style and preferences</p>
            <div className='form-group'>
              <input type='text' className='form-control' name='previousBike' value={formData.previousBike} onChange={handleInputChange} placeholder='Enter your previous bike model' required />
            </div>
            <div className='form-actions'>
              <button type='button' className='btn btn-outline' onClick={prevStep}>
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.5 7.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5z' />
                </svg>
                Back
              </button>
              <button type='button' className='btn btn-primary' onClick={nextStep}>
                Continue
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H4.5z' />
                </svg>
              </button>
            </div>
          </div>
        );
      case 3:
        return (
          <div className='form-step'>
            <h2>What is your budget for the new bike?</h2>
            <p className='form-description'>We'll find options within your price range</p>
            <div className='form-group budget-input-container'>
              <div className='slider-container'>
                <input type='range' className='form-range' name='budget' value={formData.budget} onChange={handleInputChange} min='1.5' max='50' step='0.5' />
                <div className='range-labels'>
                  <span>₹1.5 Lakhs</span>
                  <span>₹50 Lakhs</span>
                </div>
              </div>
              <div className='budget-display'>
                <p>
                  Selected Budget: <strong>₹{formData.budget} Lakhs</strong>
                </p>
              </div>
            </div>
            <div className='form-actions'>
              <button type='button' className='btn btn-outline' onClick={prevStep}>
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.5 7.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5z' />
                </svg>
                Back
              </button>
              <button type='button' className='btn btn-primary' onClick={nextStep}>
                Continue
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H4.5z' />
                </svg>
              </button>
            </div>
          </div>
        );
      case 4:
        return (
          <div className='form-step'>
            <h2>Any specific preferences?</h2>
            <p className='form-description'>Help us fine-tune your recommendations (optional)</p>
            <div className='form-group'>
              <textarea className='form-control' name='preferences' value={formData.preferences} onChange={handleInputChange} placeholder='e.g., Royal Enfield, at least 30 BHP, adventure bike, etc.' rows='4' />
              <small className='preference-hint'>You can specify brand, minimum BHP, or category preference (cruiser, street, sports, adventure)</small>
            </div>
            <div className='form-actions'>
              <button type='button' className='btn btn-outline' onClick={prevStep}>
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.5 7.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5z' />
                </svg>
                Back
              </button>
              <button type='submit' className='btn btn-primary'>
                Find My Perfect Bike
                <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M9.502 5.513a.144.144 0 0 0-.202.134V6.65a.5.5 0 0 1-.5.5H2.5v2.9h6.3a.5.5 0 0 1 .5.5v1.003c0 .108.11.176.202.134l3.984-2.933a.51.51 0 0 1 .042-.028.147.147 0 0 0 0-.252.51.51 0 0 1-.042-.028L9.502 5.513zM8.3 5.647a1.144 1.144 0 0 1 1.767-.96l3.994 2.94a1.147 1.147 0 0 1 0 1.946l-3.994 2.94a1.144 1.144 0 0 1-1.767-.96v-.503H2a.5.5 0 0 1-.5-.5v-3.9a.5.5 0 0 1 .5-.5h6.3v-.503z' />
                </svg>
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className='home-page'>
      <div className='overlay'></div>
      <div className='content'>
        <header>
          <h1>
            MotoGuru <span className='accent'>AI</span>
          </h1>
          <p>Your intelligent motorcycle advisor for personalized recommendations</p>
        </header>

        <div className='form-container'>
          <div className='card form-card'>
            {renderProgressIndicator()}

            {/* Wrap the form only for step 4, use a div for other steps */}
            {currentStep === 4 ? <form onSubmit={handleSubmit}>{renderForm()}</form> : <div>{renderForm()}</div>}
          </div>

          <div className='home-features'>
            <div className='feature'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='currentColor' viewBox='0 0 16 16'>
                <path d='M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499L12.136.326zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484L5.562 3zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-13z' />
                <path d='M8 8.5a.5.5 0 0 1 .5.5v1.5H10a.5.5 0 0 1 0 1H8.5V13a.5.5 0 0 1-1 0v-1.5H6a.5.5 0 0 1 0-1h1.5V9a.5.5 0 0 1 .5-.5z' />
              </svg>
              <h3>AI-Powered Recommendations</h3>
              <p>Our smart system analyzes your riding style and preferences to find your perfect match</p>
            </div>
            <div className='feature'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='currentColor' viewBox='0 0 16 16'>
                <path d='M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z' />
              </svg>
              <h3>Expert Analysis</h3>
              <p>Get insider knowledge with detailed pros and cons for every motorcycle suggestion</p>
            </div>
            <div className='feature'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='currentColor' viewBox='0 0 16 16'>
                <path d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM2 2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H2z' />
                <path d='M2.5 4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V4z' />
              </svg>
              <h3>Easy Comparison</h3>
              <p>Compare specs and features side-by-side to confidently choose your next ride</p>
            </div>
            <div className='feature'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='currentColor' viewBox='0 0 16 16'>
                <path d='M8 2a5.53 5.53 0 0 0-3.594 1.342c-.766.66-1.321 1.52-1.464 2.383C1.266 6.095 0 7.555 0 9.318 0 11.366 1.708 13 3.781 13h8.906C14.502 13 16 11.57 16 9.773c0-1.636-1.242-2.969-2.834-3.194C12.923 3.999 10.69 2 8 2zm2.354 5.146a.5.5 0 0 1-.708.708L8.5 6.707V10.5a.5.5 0 0 1-1 0V6.707L6.354 7.854a.5.5 0 1 1-.708-.708l2-2a.5.5 0 0 1 .708 0l2 2z' />
              </svg>
              <h3>Personalized Experience</h3>
              <p>Get recommendations tailored specifically to your budget and riding experience level</p>
            </div>
          </div>
        </div>

        {loading && (
          <div className='loading-overlay'>
            <img src='/motoGuruLogoMain.svg' alt='MotoGuru Logo' className='loading-logo' />
            <p>Finding your perfect motorcycle...</p>
          </div>
        )}
      </div>

      {modal.isOpen && <Modal message={modal.message} type={modal.type} onClose={closeModal} />}
    </div>
  );
};

export default Home;
